// Phase 194 — Plaid Link CDN widget glue (BANK-01) + OAuth redirect resumption (194 follow-up).
//
// The Plaid Link script is loaded via a <script src="https://cdn.plaid.com/..."> tag in
// connections.latte's {block scripts} (Plan 04) — NEVER bundled via npm (Plaid prohibits
// hosting their own Link script yourself). window.Plaid is set synchronously by that CDN
// script before this module's click handler can ever fire, so no timing guard is needed
// here beyond the page-guard below.
//
// Signal URLs are read from data-* attributes baked into the template by Latte's
// {link handlePlaidLinkToken!} / {link handlePlaidExchange!} macros — no hardcoded
// presenter/signal names live in this file.
//
// OAuth banks (Chase, BofA, RBC, …): when BankModel sets a redirect_uri on the link_token
// (PLAID_REDIRECT_URI env, production only), Plaid does a FULL-PAGE redirect to the bank and
// back to /transactions/connections with a ?oauth_state_id=… query param — not a popup. We
// stash the in-flight link_token + callback context in localStorage before opening Link, then
// on page load re-create the handler with receivedRedirectUri to resume the exact same
// session. When no redirect_uri is set (sandbox / not configured) Plaid uses its popup OAuth
// flow, oauth_state_id never appears, and the resumption path below never fires.

declare global {
    interface Window {
        Plaid: {
            create(config: {
                token: string;
                receivedRedirectUri?: string;
                onSuccess: (publicToken: string, metadata: PlaidMetadata) => void;
                onExit: (err: PlaidError | null, metadata: PlaidExitMetadata) => void;
                onLoad?: () => void;
                onEvent?: (eventName: string, metadata: Record<string, unknown>) => void;
            }): { open: () => void; destroy: () => void };
        };
    }
}

interface PlaidMetadata {
    institution: { name: string; institution_id: string };
    accounts: Array<{ id: string; name: string; mask: string; type: string; subtype: string }>;
    link_session_id: string;
}
interface PlaidError { error_code: string; error_message: string }
interface PlaidExitMetadata { status: string; link_session_id: string }

// Persisted across the OAuth redirect round-trip. localStorage survives the full-page
// navigation to the bank and back that a redirect_uri-based OAuth flow performs (sessionStorage
// would too, but localStorage is the shape Plaid's own examples use).
const OAUTH_CTX_KEY = 'plaid_oauth_ctx';
interface ConnectCtx { mode: 'connect'; token: string; exchangeUrl: string; exchangeSignal: string }
interface ReauthCtx  { mode: 'reauth';  token: string; bankItemId: string; resolvedUrl: string }
type OAuthCtx = ConnectCtx | ReauthCtx;

export default class {
    static init = () => {
        const post = (url: string, body: Record<string, string>) =>
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(body),
            });

        // Open Link for a NEW connection. receivedRedirectUri is set ONLY when resuming an
        // OAuth redirect (never on the initial click).
        const openConnect = (ctx: ConnectCtx, receivedRedirectUri?: string) => {
            localStorage.setItem(OAUTH_CTX_KEY, JSON.stringify(ctx));
            const handler = window.Plaid.create({
                token: ctx.token,
                ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
                onSuccess: async (publicToken, metadata) => {
                    // Exchange public_token server-side (handlePlaidExchange). The raw
                    // public_token is never stored client-side beyond this single POST.
                    localStorage.removeItem(OAUTH_CTX_KEY);
                    await post(ctx.exchangeUrl, {
                        _do: ctx.exchangeSignal,
                        public_token: publicToken,
                        metadata: JSON.stringify(metadata),
                    });
                    window.location.reload();
                },
                onExit: () => { localStorage.removeItem(OAUTH_CTX_KEY); },
            });
            handler.open();
        };

        // Open Link in update mode for re-auth (BANK-07). NO public_token exchange —
        // onSuccess only clears requires_reauth via handlePlaidReauthResolved.
        const openReauth = (ctx: ReauthCtx, receivedRedirectUri?: string) => {
            localStorage.setItem(OAUTH_CTX_KEY, JSON.stringify(ctx));
            const handler = window.Plaid.create({
                token: ctx.token,
                ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
                onSuccess: async () => {
                    localStorage.removeItem(OAUTH_CTX_KEY);
                    await post(ctx.resolvedUrl, { bank_item_id: ctx.bankItemId });
                    window.location.reload();
                },
                onExit: () => { localStorage.removeItem(OAUTH_CTX_KEY); },
            });
            handler.open();
        };

        // ── Initial connect (BANK-01) ────────────────────────────────────────────────
        const connectBtn = document.getElementById('plaid-connect-btn');
        if (connectBtn) {
            connectBtn.addEventListener('click', async () => {
                // Mint link_token via AJAX-POST signal (handlePlaidLinkToken).
                const mintResp = await post(connectBtn.dataset.mintUrl!, { _do: connectBtn.dataset.mintSignal! });
                const mintJson = await mintResp.json();
                const linkToken: string | undefined = mintJson.link_token;
                if (!linkToken) {
                    return;  // mint failed server-side — connections.latte shows the "not configured" state
                }
                openConnect({
                    mode: 'connect',
                    token: linkToken,
                    exchangeUrl: connectBtn.dataset.exchangeUrl!,
                    exchangeSignal: connectBtn.dataset.exchangeSignal!,
                });
            });
        }

        // ── BANK-07 re-auth (update mode) — bind every [data-reauth-url] button ────────
        // connections.latte renders one per bank_items row with status='requires_reauth'.
        document.querySelectorAll<HTMLElement>('[data-reauth-url]').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const bankItemId = btn.dataset.bankItemId!;
                const reauthResp = await post(btn.dataset.reauthUrl!, { bank_item_id: bankItemId });
                const reauthJson = await reauthResp.json();

                if (reauthJson.error) {
                    // Decrypt-null / mint failure (Pitfall 5) — show the server's
                    // disconnect-and-reconnect message rather than a raw Plaid error.
                    alert(reauthJson.error);
                    return;
                }
                const linkToken: string | undefined = reauthJson.link_token;
                if (!linkToken) return;

                openReauth({ mode: 'reauth', token: linkToken, bankItemId, resolvedUrl: btn.dataset.resolvedUrl! });
            });
        });

        // ── D-07 per-account include_in_sync toggle — no re-auth needed ───────────────
        document.querySelectorAll<HTMLInputElement>('.js-plaid-toggle').forEach((toggle) => {
            toggle.addEventListener('change', async () => {
                await post(toggle.dataset.toggleUrl!, {
                    account_id: toggle.dataset.accountId!,
                    include: toggle.checked ? '1' : '0',
                });
            });
        });

        // ── OAuth redirect resumption ─────────────────────────────────────────────────
        // An OAuth bank redirected back to redirect_uri (/transactions/connections) with a
        // ?oauth_state_id=… param. Resume the SAME Link session with receivedRedirectUri; the
        // in-flight token + callbacks were stashed in localStorage before we opened Link.
        if (new URLSearchParams(window.location.search).has('oauth_state_id')) {
            const receivedRedirectUri = window.location.href;
            let ctx: OAuthCtx | null = null;
            try { ctx = JSON.parse(localStorage.getItem(OAUTH_CTX_KEY) || 'null'); } catch { ctx = null; }
            if (ctx && ctx.token) {
                if (ctx.mode === 'reauth') openReauth(ctx, receivedRedirectUri);
                else openConnect(ctx, receivedRedirectUri);
            }
            // Strip the OAuth params so a manual refresh doesn't replay a stale resume.
            // Safe now: receivedRedirectUri (the full href) was already captured + passed to create().
            window.history.replaceState({}, '', window.location.pathname);
        }
    }
}
